import { useState } from 'react';
import type { ReactElement } from 'react';

import { get, patch } from 'utils/network';
import { ApiRoute, ErrorMessage } from 'config';

import { useUpcomingMenu } from 'contexts/UpcomingMenu';
import type { IUpcomingMenu } from 'models/UpcomingMenu';
import IApiResponse from 'models/IApiResponse';
import Address from 'models/Address';

import RadioButton from 'components/shared/RadioButton';
import ValidableControl from 'components/shared/ValidableControl';
import ShipToModal from 'components/shared/modals/ShipToModal';

type Recipient = 'friend' | 'me';
const emailRegexp = /[\w+-.]+@[a-z\d-]+(\.[a-z\d-]+)*\.[a-z]+/i;

type Props = {
  show: boolean;
  recipient: Recipient;
  onRecipientChange: (rcpt: Recipient) => void;
  onAddressUpdate?: (address: Address, date?: Date) => void;
  onShow;
  onClose;
};

function ShipToRecipientModal({
  show,
  recipient,
  onRecipientChange,
  onAddressUpdate,
  onShow,
  onClose,
}: Props): ReactElement {
  const { upcomingMenu, dispatch } = useUpcomingMenu();
  const [responseError, setResponseError] = useState(null);
  const [recipientEmailError, setRecipientEmailError] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState('');

  const isRecipientEmailValid = () => (
    recipient === 'me'
    || (recipientEmail.length && emailRegexp.test(recipientEmail))
  );

  const handleAddressChange = async (address: Address) => {
    const { cart_id: cartId, menu_id: menuId } = upcomingMenu;

    if (isRecipientEmailValid()) {
      setRecipientEmailError(null);

      const response = await patch(`${ApiRoute.CARTS}/${cartId}`, {
        cart: {
          shipping_address_id: address?.id,
          recipient_email: recipient === 'friend' ? recipientEmail : undefined,
          rebuttal_unskip: true,
        },
      });

      if (response.status === 422) {
        // @ts-expect-error TODO: fix null safety
        setResponseError((response.parsedBody as IApiResponse).details);
        document.getElementsByClassName('modal')[0].scrollTop = 0;
      } else {
        setResponseError(null);

        const menu = (await get<IUpcomingMenu>(
          `${ApiRoute.UPCOMING_MENUS}/${menuId}`,
        )).parsedBody;
          // @ts-expect-error TODO: fix null safety
        dispatch({ type: 'reload', data: menu });

        if (onAddressUpdate) {
          // @ts-expect-error TODO: fix null safety
          onAddressUpdate(address, menu.delivery_on);
        }

        onClose();
      }
    } else {
      // @ts-expect-error TODO: fix null safety
      setRecipientEmailError(ErrorMessage.FORGOT_EMAIL);
    }
  };

  return (
    <ShipToModal
      show={show}
      onShow={onShow}
      onClose={onClose}
      onChange={handleAddressChange}
      baseError={responseError ?? undefined}
      selectedId={upcomingMenu.shipping_address.id ?? null}
      bodyHeader={(
        <>
          <div className="c-heading c-heading--brand-8">
            RECIPIENT
          </div>
          <div
            className={[
              'c-scheduled-addresses__recipient-mode-wrapper',
              'is-active',
              'clearfix',
            ].join(' ')}
          >
            <div className="c-form-group--inline">
              <RadioButton
                id="ship-me"
                label="Me"
                name="recipient"
                onChange={() => onRecipientChange('me')}
                checked={recipient === 'me'}
              />
              <RadioButton
                id="ship-friend"
                label="A friend"
                name="recipient"
                onChange={() => onRecipientChange('friend')}
                checked={recipient === 'friend'}
              />
            </div>
            {recipient === 'friend' && (
              <ValidableControl
                className="c-scheduled-addresses__recipient-email clearfix"
                isInvalid={!!recipientEmailError}
                errorMessage={recipientEmailError}
              >
                <input
                  name="recipient_email"
                  placeholder="Enter friend's Email"
                  type="email"
                  id="order_recipient_email"
                  onChange={(e) => setRecipientEmail(e.target.value)}
                />
              </ValidableControl>
            )}
          </div>
        </>
      )}
    />
  );
}

export default ShipToRecipientModal;
